import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Card, 
    CardContent, 
    Typography, 
    TextField, 
    Button, 
    Switch, 
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Alert,
    IconButton,
    Grid,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Autocomplete,
} from '@mui/material';
import { TIMEZONES } from '../utils/timezones';
import { ExpandMore, Lock, Notifications, NotificationsActive, Settings as SettingsIcon, Delete, Add, Email, CalendarMonth } from '@mui/icons-material';
import styled from '@emotion/styled';
import { updateUser, updateUserPassword } from '../redux/slices/users';
import { setCurrentUser } from '../redux/slices/auth';
import { setup2FA, enable2FA, disable2FA, regenerateBackupCodes, getTrustedDevices, revokeTrustedDevice, revokeAllTrustedDevices } from '../redux/ducks/twoFactor';
import { QRCodeSVG } from 'qrcode.react';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import HoverTooltip from '../components/HoverTooltip';
import PageHeader from '../components/PageHeader';
import ApiKeysCard from '../components/ApiKeysCard';
import { usePushSubscription } from '../components/PushNotificationToggle';
import { getConfigValue } from '../redux/slices/config';
import { getCalendarStatus } from '../redux/slices/calendar';
import { useHasPermission } from '../utils/permissionUtils';
import { formatDateTime } from '../utils/dateUtils';

const SettingsContainer = styled(Box)(({ theme }) => ({
    maxWidth: theme.breakpoints.values.lg,
    width: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3),
    }
}));

const SettingsCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[2],
}));

const SecurityGridContainer = styled(Grid)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flex: '1 1 auto',
    minWidth: 0,
}));

const SectionTitleText = styled(ListItemText)(({ theme }) => ({
    '& .MuiListItemText-primary': {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.h6,
        color: theme.palette.text.primary,
        overflowWrap: 'break-word',
    },
    '& .MuiListItemText-secondary': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2,
        color: theme.palette.text.secondary,
        overflowWrap: 'break-word',
    },
    marginLeft: theme.spacing(2),
    minWidth: 0,
    flex: '1 1 auto',
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
}));

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const PasswordField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const TwoFactorOption = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
}));

const TwoFactorOptionLabel = styled(Box)(({ theme }) => ({
    flex: 1,
}));

const TwoFactorOptionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(0.5),
}));

const TwoFactorOptionStatus = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
}));

const ChannelTitle = styled(ListItemText)(({ theme }) => ({
    '& .MuiListItemText-primary': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2,
        color: theme.palette.text.primary,
    },
    '& .MuiListItemText-secondary': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2,
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& > .MuiButton-root': {
        flexShrink: 0,
    },
}));

const ChannelItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
}));

const ChannelActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const CategoryDescription = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const PreferenceOption = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(1),
}));

const PreferenceOptionLabel = styled(Box)(({ theme }) => ({
    flex: 1,
    marginRight: theme.spacing(2),
    cursor: 'pointer',
    userSelect: 'none',
}));

const PreferenceOptionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    marginBottom: theme.spacing(0.25),
}));

const PageFooter = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const TwoFactorDescription = styled(SectionDescription)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const TwoFactorAlert = styled(Alert)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const QRCodeBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
}));

const SecretBox = styled(Box)(({ theme }) => ({
    fontFamily: 'monospace',
    fontSize: theme.typography.size.lg,
    letterSpacing: '0.15em',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    wordBreak: 'break-all',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
}));

const BackupCodeGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
}));

const BackupCodeText = styled(Typography)(({ theme }) => ({
    fontFamily: 'monospace',
    fontSize: theme.typography.size.base,
    letterSpacing: '0.1em',
    textAlign: 'center',
}));

const TrustedDeviceRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
}));

const TrustedDeviceMeta = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
}));

const TrustedDeviceLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const TrustedDeviceSubLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const CopySecretRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
}));

const TwoFactorWarningAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

function formatSecretGroups(secret) {
    if (!secret) return '';
    return secret.replace(/(.{4})/g, '$1 ').trim();
}

function parseUserAgent(label) {
    if (!label || typeof label !== 'string') return 'Unknown device';
    const ua = label;
    let browser = '';
    if (/Edg\//i.test(ua)) browser = 'Edge';
    else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
    else if (/Firefox\//i.test(ua)) browser = 'Firefox';
    else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
    let os = '';
    if (/Windows NT/i.test(ua)) os = 'Windows';
    else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iOS/i.test(ua)) os = 'iOS';
    else if (/Linux/i.test(ua)) os = 'Linux';
    if (browser && os) return `${browser} on ${os}`;
    if (browser) return browser;
    if (os) return os;
    return 'Unknown device';
}

const PreferencesDescription = styled(SectionDescription)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const NotificationAccordion = styled(Accordion)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const AccordionSummaryContent = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const AccordionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const AccordionDescription = styled(CategoryDescription)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
}));

const NotificationEventBlock = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const NotificationEventTitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
}));

const PreferenceOptionLabelDisabled = styled(PreferenceOptionLabel, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ disabled }) => ({
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
}));

const SettingsDescription = styled(SectionDescription)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SettingsFormControl = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const NumberFormatField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FirstFormField = styled(FormField)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const CalendarStatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
}));

const CalendarStatusLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
}));

const CalendarStatusValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
}));

const CalendarOptOutNote = styled(Alert)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const Settings = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const push = usePushSubscription();
    const canSeeInternal = hasPermission('canSeeInternal');
    const calendarStatus = useSelector(state => state.calendar?.status);
    const currentUser = useSelector(state => state.auth.currentUser);
    const userId = useSelector(state => state.auth.userId);
    const permissions = useSelector(state => state.auth.permissions);
    const systemRole = useSelector(state => state.auth.systemRole);
    const trustedDevices = useSelector(state => state.auth.trustedDevices);
    const twoFactorEnabled = !!currentUser?.twoFactor?.enabled;

    const [twoFactorDialog, setTwoFactorDialog] = useState({ open: false, mode: null, step: 1 });
    const [twoFactorForm, setTwoFactorForm] = useState({ password: '', code: '', secret: '', otpauthUrl: '' });
    const [twoFactorBackupCodes, setTwoFactorBackupCodes] = useState([]);
    const [twoFactorError, setTwoFactorError] = useState('');
    const [twoFactorBusy, setTwoFactorBusy] = useState(false);
    const [revokeDeviceTarget, setRevokeDeviceTarget] = useState(null);
    const [revokeAllOpen, setRevokeAllOpen] = useState(false);

    useEffect(() => {
        if (twoFactorEnabled) {
            dispatch(getTrustedDevices());
        }
    }, [dispatch, twoFactorEnabled]);

    useEffect(() => {
        if (canSeeInternal) {
            dispatch(getCalendarStatus());
        }
    }, [dispatch, canSeeInternal]);
    
    // Password state
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Notification channels state
    const [notificationChannels, setNotificationChannels] = useState([]);
    
    // Add channel dialog state
    const [addChannelDialogOpen, setAddChannelDialogOpen] = useState(false);
    const [newChannel, setNewChannel] = useState({ name: '', url: '' });
    const [channelErrors, setChannelErrors] = useState({});

    // Preferences state
    const [preferences, setPreferences] = useState({
        email: "",
        timezone: "UTC",
        notifications: {
            email: {},
            apprise: {},
        },
    });

    // Notification types state, derived from the code registry metadata (not DB content).
    // { [objectType]: [eventType, ...] } — only user-optional events that support a toggleable channel.
    const [notificationTypes, setNotificationTypes] = useState({});
    // Per-event metadata for labels and supported channels: { [objectType]: { [eventType]: { label, channels } } }
    const [notificationMeta, setNotificationMeta] = useState({});

    // Collapsed state for notification groups (all collapsed by default)
    const [expandedGroups, setExpandedGroups] = useState({});

    // Load the notification event registry (metadata only — enumerated from code)
    useEffect(() => {
        dispatch(getConfigValue({
            key: 'notificationTemplatesEmail',
            value: null,
            callback: (response) => {
                const registry = response?.value;
                if (!registry || typeof registry !== 'object') {
                    return;
                }
                const types = {};
                const meta = {};

                Object.keys(registry).forEach((objectType) => {
                    const events = registry[objectType];
                    if (!events || typeof events !== 'object') return;

                    const eventTypes = [];
                    Object.keys(events).forEach((eventType) => {
                        const def = events[eventType];
                        if (!def || typeof def !== 'object') return;
                        const channels = Array.isArray(def.channels) ? def.channels : [];
                        // Only optional events with a channel a user can toggle (email or apprise) get a toggle.
                        const toggleable = channels.includes('email') || channels.includes('apprise');
                        if (def.optional !== true || !toggleable) return;
                        eventTypes.push(eventType);
                        meta[objectType] = meta[objectType] || {};
                        meta[objectType][eventType] = { label: def.label || eventType, channels };
                    });

                    if (eventTypes.length > 0) {
                        types[objectType] = eventTypes;
                    }
                });

                setNotificationTypes(types);
                setNotificationMeta(meta);
                setExpandedGroups((prev) => {
                    const newGroups = { ...prev };
                    Object.keys(types).forEach((type) => {
                        if (!(type in newGroups)) {
                            newGroups[type] = false;
                        }
                    });
                    Object.keys(newGroups).forEach((type) => {
                        if (!(type in types)) {
                            delete newGroups[type];
                        }
                    });
                    return newGroups;
                });
            }
        }));
    }, [dispatch]);

    // Initialize preferences from current user
    useEffect(() => {
        if (currentUser) {
            const existingNotifications = currentUser.preferences?.notifications || {};
            
            // Initialize notification channels from preferences
            const savedChannels = currentUser.preferences?.notificationChannels || [];
            setNotificationChannels(savedChannels);
            
            // Build notification state structure
            const emailNotifications = {};
            const appriseNotifications = {};
            
            // Initialize all notification types
            Object.keys(notificationTypes).forEach(objectType => {
                emailNotifications[objectType] = {};
                appriseNotifications[objectType] = {};
                
                notificationTypes[objectType].forEach(eventType => {
                    // Check if preference exists (defaults to true if not set)
                    emailNotifications[objectType][eventType] = 
                        existingNotifications[objectType]?.[eventType]?.email !== false;
                    appriseNotifications[objectType][eventType] = 
                        existingNotifications[objectType]?.[eventType]?.apprise !== false;
                });
            });

            setPreferences({
                email: currentUser.email || "",
                timezone: currentUser.timezone || "UTC",
                notifications: {
                    email: emailNotifications,
                    apprise: appriseNotifications,
                },
            });
        }
    }, [currentUser, notificationTypes]);

    const handlePasswordChange = (field, value) => {
        setPasswordData(prev => ({ ...prev, [field]: value }));
        if (passwordErrors[field]) {
            setPasswordErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validatePassword = () => {
        const errors = {};
        
        if (!passwordData.currentPassword) {
            errors.currentPassword = 'Current password is required';
        }
        
        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters';
        }
        
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        
        if (passwordData.currentPassword === passwordData.newPassword) {
            errors.newPassword = 'New password must be different from current password';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUpdatePassword = () => {
        if (!validatePassword()) {
            return;
        }

        setIsChangingPassword(true);
        dispatch(updateUserPassword({
            userId,
            password: passwordData.newPassword,
            currentPassword: passwordData.currentPassword,
            callback: (response) => {
                setIsChangingPassword(false);
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                });
                setPasswordErrors({});
            }
        }));
    };

    const handlePreferenceChange = (field, value) => {
        setPreferences(prev => ({ ...prev, [field]: value }));
    };

    const handleNotificationChange = (channel, objectType, eventType, checked) => {
        setPreferences(prev => ({
            ...prev,
            notifications: {
                ...prev.notifications,
                [channel]: {
                    ...prev.notifications[channel],
                    [objectType]: {
                        ...prev.notifications[channel][objectType],
                        [eventType]: checked,
                    },
                },
            },
        }));
    };

    const handleAddChannel = () => {
        setNewChannel({ name: '', url: '' });
        setChannelErrors({});
        setAddChannelDialogOpen(true);
    };

    const handleCloseAddChannelDialog = () => {
        setAddChannelDialogOpen(false);
        setNewChannel({ name: '', url: '' });
        setChannelErrors({});
    };

    const validateChannel = () => {
        const errors = {};
        if (!newChannel.name.trim()) {
            errors.name = 'Name is required';
        }
        if (!newChannel.url.trim()) {
            errors.url = 'URL is required';
        } else if (!newChannel.url.match(/^[a-z]+:\/\/.+/i)) {
            errors.url = 'URL must be a valid Apprise URL (e.g., discord://webhook_id/webhook_token)';
        }
        setChannelErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveChannel = () => {
        if (!validateChannel()) {
            return;
        }

        const channel = {
            id: Date.now().toString(),
            name: newChannel.name.trim(),
            url: newChannel.url.trim(),
            enabled: true,
        };
        setNotificationChannels([...notificationChannels, channel]);
        handleCloseAddChannelDialog();
    };

    const handleDeleteChannel = (channelId) => {
        setNotificationChannels(notificationChannels.filter(ch => ch.id !== channelId));
    };

    const handleToggleChannel = (channelId) => {
        setNotificationChannels(notificationChannels.map(ch => 
            ch.id === channelId ? { ...ch, enabled: !ch.enabled } : ch
        ));
    };

    const notificationCategories = React.useMemo(() => {
        const categories = {};

        Object.keys(notificationTypes).forEach(objectType => {
            const eventTypes = notificationTypes[objectType];
            if (eventTypes && eventTypes.length > 0) {
                const objectTypeLabel = objectType.replace(/([A-Z])/g, ' $1').trim();
                categories[objectType] = {
                    description: `Notifications for ${objectTypeLabel.toLowerCase()} events.`,
                    events: {
                        [objectType]: eventTypes,
                    },
                };
            }
        });

        return categories;
    }, [notificationTypes]);

    const handleSavePreferences = () => {
        // Build notifications structure in the format expected by the backend
        // Format: notifications[objectType][eventType][channel] = true/false
        const notificationPreferences = {};
        
        Object.keys(notificationTypes).forEach(objectType => {
            notificationPreferences[objectType] = {};
            notificationTypes[objectType].forEach(eventType => {
                notificationPreferences[objectType][eventType] = {
                    email: preferences.notifications.email[objectType]?.[eventType] ?? true,
                    apprise: preferences.notifications.apprise[objectType]?.[eventType] ?? true,
                };
            });
        });

        const updateData = {
            timezone: preferences.timezone,
            preferences: {
                ...(currentUser.preferences || {}),
                notifications: {
                    ...notificationPreferences,
                },
                notificationChannels: notificationChannels || [],
            }
        };

        dispatch(updateUser({
            userId,
            userData: updateData,
            callback: (response) => {
                if (response?.user) {
                    dispatch(setCurrentUser({
                        user: response.user,
                        id: userId,
                        role: systemRole,
                        permissions: permissions,
                    }));
                }
            }
        }));
    };

    const closeTwoFactorDialog = () => {
        setTwoFactorDialog({ open: false, mode: null, step: 1 });
        setTwoFactorForm({ password: '', code: '', secret: '', otpauthUrl: '' });
        setTwoFactorBackupCodes([]);
        setTwoFactorError('');
        setTwoFactorBusy(false);
    };

    const startEnableTwoFactor = () => {
        setTwoFactorError('');
        setTwoFactorBusy(true);
        dispatch(setup2FA((data) => {
            setTwoFactorBusy(false);
            if (data?.error || data?.status !== 1) {
                setTwoFactorError(data?.error || data?.msg || 'Could not start 2FA setup.');
                return;
            }
            setTwoFactorForm({ password: '', code: '', secret: data.secret, otpauthUrl: data.otpauthUrl });
            setTwoFactorDialog({ open: true, mode: 'enable', step: 1 });
        }));
    };

    const submitEnableTwoFactor = () => {
        if (!twoFactorForm.password || !twoFactorForm.code) {
            setTwoFactorError('Password and code are required.');
            return;
        }
        setTwoFactorError('');
        setTwoFactorBusy(true);
        dispatch(enable2FA(twoFactorForm.password, twoFactorForm.code.trim(), (data) => {
            setTwoFactorBusy(false);
            if (data?.error || data?.status !== 1) {
                setTwoFactorError(data?.error || data?.msg || 'Could not enable 2FA.');
                return;
            }
            setTwoFactorBackupCodes(data.backupCodes || []);
            setTwoFactorDialog({ open: true, mode: 'enable', step: 3 });
        }));
    };

    const startDisableTwoFactor = () => {
        setTwoFactorForm({ password: '', code: '', secret: '', otpauthUrl: '' });
        setTwoFactorError('');
        setTwoFactorDialog({ open: true, mode: 'disable', step: 1 });
    };

    const submitDisableTwoFactor = () => {
        if (!twoFactorForm.password || !twoFactorForm.code) {
            setTwoFactorError('Password and code are required.');
            return;
        }
        setTwoFactorError('');
        setTwoFactorBusy(true);
        dispatch(disable2FA(twoFactorForm.password, twoFactorForm.code.trim(), (data) => {
            setTwoFactorBusy(false);
            if (data?.error || data?.status !== 1) {
                setTwoFactorError(data?.error || data?.msg || 'Could not disable 2FA.');
                return;
            }
            closeTwoFactorDialog();
        }));
    };

    const startRegenerateBackupCodes = () => {
        setTwoFactorForm({ password: '', code: '', secret: '', otpauthUrl: '' });
        setTwoFactorError('');
        setTwoFactorDialog({ open: true, mode: 'regenerate', step: 1 });
    };

    const submitRegenerateBackupCodes = () => {
        if (!twoFactorForm.password || !twoFactorForm.code) {
            setTwoFactorError('Password and code are required.');
            return;
        }
        setTwoFactorError('');
        setTwoFactorBusy(true);
        dispatch(regenerateBackupCodes(twoFactorForm.password, twoFactorForm.code.trim(), (data) => {
            setTwoFactorBusy(false);
            if (data?.error || data?.status !== 1) {
                setTwoFactorError(data?.error || data?.msg || 'Could not regenerate backup codes.');
                return;
            }
            setTwoFactorBackupCodes(data.backupCodes || []);
            setTwoFactorDialog({ open: true, mode: 'regenerate', step: 3 });
        }));
    };

    const copyBackupCodes = () => {
        if (!twoFactorBackupCodes.length) return;
        const text = twoFactorBackupCodes.join('\n');
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => {});
        }
    };

    const copySecret = () => {
        if (!twoFactorForm.secret) return;
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(twoFactorForm.secret).catch(() => {});
        }
    };

    const handleRevokeTrustedDevice = () => {
        if (!revokeDeviceTarget) return;
        dispatch(revokeTrustedDevice({ id: revokeDeviceTarget.id }));
        setRevokeDeviceTarget(null);
    };

    const handleRevokeAllTrustedDevices = () => {
        dispatch(revokeAllTrustedDevices());
        setRevokeAllOpen(false);
    };

    if (!currentUser) {
        return (
            <SettingsContainer>
                <PageHeader title="Settings" />
                <Alert severity="info">Loading settings...</Alert>
            </SettingsContainer>
        );
    }

    return (
        <SettingsContainer>
            <PageHeader title="Settings" />

            {/* Security Section */}
            <SettingsCard>
                <CardContent>
                    <SectionTitle>
                        <Lock fontSize="medium" />
                        <SectionTitleText primary="Security" secondary="Update your account password and add an extra layer of security to your account." />
                    </SectionTitle>

                    <SecurityGridContainer container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                Change Password
                            </Typography>
                            <PasswordField
                                fullWidth
                                type="password"
                                label="Current Password"
                                value={passwordData.currentPassword}
                                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                                error={!!passwordErrors.currentPassword}
                                helperText={passwordErrors.currentPassword}
                                disabled={isChangingPassword}
                                autoComplete="off"
                            />
                            <PasswordField
                                fullWidth
                                type="password"
                                label="New Password"
                                value={passwordData.newPassword}
                                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                                error={!!passwordErrors.newPassword}
                                helperText={passwordErrors.newPassword || 'Must be at least 8 characters'}
                                disabled={isChangingPassword}
                                autoComplete="new-password"
                            />
                            <PasswordField
                                fullWidth
                                type="password"
                                label="Confirm New Password"
                                value={passwordData.confirmPassword}
                                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                                error={!!passwordErrors.confirmPassword}
                                helperText={passwordErrors.confirmPassword}
                                disabled={isChangingPassword}
                                autoComplete="new-password"
                            />
                            <Button
                                variant="contained"
                                onClick={handleUpdatePassword}
                                disabled={isChangingPassword || !passwordData.newPassword}
                                fullWidth
                            >
                                {isChangingPassword ? 'Changing Password...' : 'Update Password'}
                            </Button>
                        </Grid>

                        {/* Two-Factor Authentication Section */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                Two-Factor Authentication
                            </Typography>
                            <TwoFactorDescription variant="body2">
                                Add an extra layer of security to your account with an authenticator app (TOTP).
                            </TwoFactorDescription>

                            <TwoFactorOption>
                                <TwoFactorOptionLabel>
                                    <TwoFactorOptionTitle>Authenticator App</TwoFactorOptionTitle>
                                    <TwoFactorOptionStatus>{twoFactorEnabled ? 'Enabled' : 'Not enabled'}</TwoFactorOptionStatus>
                                </TwoFactorOptionLabel>
                                {twoFactorEnabled ? (
                                    <Button variant="outlined" color="error" size="small" onClick={startDisableTwoFactor} disabled={twoFactorBusy}>
                                        Disable
                                    </Button>
                                ) : (
                                    <Button variant="contained" size="small" onClick={startEnableTwoFactor} disabled={twoFactorBusy}>
                                        Enable
                                    </Button>
                                )}
                            </TwoFactorOption>

                            {twoFactorEnabled && (
                                <TwoFactorOption>
                                    <TwoFactorOptionLabel>
                                        <TwoFactorOptionTitle>Backup Codes</TwoFactorOptionTitle>
                                        <TwoFactorOptionStatus>One-time recovery codes</TwoFactorOptionStatus>
                                    </TwoFactorOptionLabel>
                                    <Button variant="outlined" size="small" onClick={startRegenerateBackupCodes} disabled={twoFactorBusy}>
                                        Regenerate
                                    </Button>
                                </TwoFactorOption>
                            )}

                            {!twoFactorEnabled && (
                                <TwoFactorAlert severity="info">
                                    Use an authenticator app like 1Password, Authy, or Google Authenticator.
                                </TwoFactorAlert>
                            )}
                        </Grid>
                    </SecurityGridContainer>
                </CardContent>
            </SettingsCard>

            {twoFactorEnabled && (
                <SettingsCard>
                    <CardContent>
                        <SectionHeader>
                            <SectionTitle>
                                <Lock fontSize="medium" />
                                <SectionTitleText primary="Trusted Devices" secondary="Devices that can skip 2FA for 30 days after verification." />
                            </SectionTitle>
                            {trustedDevices?.length > 0 && (
                                <Button variant="outlined" color="error" size="small" onClick={() => setRevokeAllOpen(true)}>
                                    Revoke All
                                </Button>
                            )}
                        </SectionHeader>
                        {(!trustedDevices || trustedDevices.length === 0) && (
                            <Alert severity="info">No trusted devices yet. Tick "Remember this device" during 2FA verification to add one.</Alert>
                        )}
                        {trustedDevices?.map((device) => {
                            const friendlyName = parseUserAgent(device.label);
                            return (
                                <TrustedDeviceRow key={device.id}>
                                    <TrustedDeviceMeta>
                                        <TrustedDeviceLabel title={device.label}>{friendlyName}</TrustedDeviceLabel>
                                        <TrustedDeviceSubLabel>
                                            Added {formatDateTime(device.createdAt) || '—'}
                                            {device.lastUsedAt ? ` · Last used ${formatDateTime(device.lastUsedAt)}` : ''}
                                            {device.expiresAt ? ` · Expires ${formatDateTime(device.expiresAt)}` : ''}
                                        </TrustedDeviceSubLabel>
                                    </TrustedDeviceMeta>
                                    <HoverTooltip title="Revoke device">
                                        <IconButton size="small" color="error" aria-label="Revoke device" onClick={() => setRevokeDeviceTarget({ id: device.id, name: friendlyName })}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </HoverTooltip>
                                </TrustedDeviceRow>
                            );
                        })}
                    </CardContent>
                </SettingsCard>
            )}

            <ApiKeysCard />

            {canSeeInternal && (
                <SettingsCard>
                    <CardContent>
                        <SectionTitle>
                            <CalendarMonth fontSize="medium" />
                            <SectionTitleText primary="Calendar" secondary="Status of your Google Workspace calendar integration." />
                        </SectionTitle>
                        <CalendarStatusRow>
                            <CalendarStatusLabel>Calendar service</CalendarStatusLabel>
                            <CalendarStatusValue>{calendarStatus?.enabled ? 'Enabled' : 'Disabled'}</CalendarStatusValue>
                        </CalendarStatusRow>
                        <CalendarStatusRow>
                            <CalendarStatusLabel>Connection</CalendarStatusLabel>
                            <CalendarStatusValue>{calendarStatus?.connected ? 'Connected' : 'Not connected'}</CalendarStatusValue>
                        </CalendarStatusRow>
                        <CalendarStatusRow>
                            <CalendarStatusLabel>Opt-out</CalendarStatusLabel>
                            <CalendarStatusValue>{calendarStatus?.optOut ? 'Active' : 'Inactive'}</CalendarStatusValue>
                        </CalendarStatusRow>
                        <CalendarOptOutNote severity="info">
                            Calendar opt-out is managed by an administrator. Contact your administrator to change.
                        </CalendarOptOutNote>
                    </CardContent>
                </SettingsCard>
            )}

            {/* Notification Channels Section */}
            <SettingsCard>
                <CardContent>
                    <SectionHeader>
                        <SectionTitle>
                            <Notifications fontSize="medium" />
                            <SectionTitleText primary="Notification Channels" secondary="Configure notification services."  />
                        </SectionTitle>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAddChannel}
                            size="small"
                        >
                            Add Channel
                        </Button>
                    </SectionHeader>

                    <ChannelItem key={currentUser?.id}>
                        <Email fontSize="small" />
                        <ChannelTitle primary="Email" secondary={currentUser?.email} />
                        <ChannelActions>
                            <Switch
                                checked={true}
                                size="small"
                                disabled
                            />
                        </ChannelActions>
                    </ChannelItem>

                    {(push.available || push.blocked) && (
                        <ChannelItem>
                            <NotificationsActive fontSize="small" />
                            <ChannelTitle primary="Push notifications on this device" secondary={push.statusText} />
                            <ChannelActions>
                                <Switch
                                    checked={push.subscribed}
                                    onChange={push.toggle}
                                    disabled={push.busy || push.blocked}
                                    size="small"
                                    inputProps={{ "aria-label": "Push notifications on this device" }}
                                />
                            </ChannelActions>
                        </ChannelItem>
                    )}

                    {notificationChannels.length > 0 && (
                        notificationChannels.map((channel) => (
                            <ChannelItem key={channel.id}>
                                <Notifications fontSize="small" />
                                <ChannelTitle primary={channel.name} secondary={channel.url || 'No URL configured'} />
                                <ChannelActions>
                                    <Switch
                                        checked={channel.enabled}
                                        onChange={() => handleToggleChannel(channel.id)}
                                        size="small"
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteChannel(channel.id)}
                                        color="error"
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </ChannelActions>
                            </ChannelItem>
                        ))
                    )}
                </CardContent>
            </SettingsCard>

            {/* Notification Preferences Section */}
            <SettingsCard>
                <CardContent>
                    <SectionTitle>
                        <Notifications fontSize="small" />
                        <SectionTitleText variant="h6">Notification Preferences</SectionTitleText>
                    </SectionTitle>
                    <PreferencesDescription variant="body2">
                        Choose which notifications you want to receive through each channel.
                    </PreferencesDescription>

                    {Object.keys(notificationCategories).map((categoryName) => {
                        const category = notificationCategories[categoryName];
                        const isExpanded = expandedGroups[categoryName] || false;
                        
                        return (
                            <NotificationAccordion
                                key={categoryName}
                                expanded={isExpanded}
                                onChange={(event, expanded) => {
                                    setExpandedGroups(prev => ({
                                        ...prev,
                                        [categoryName]: expanded
                                    }));
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore />}
                                    aria-controls={`${categoryName}-content`}
                                    id={`${categoryName}-header`}
                                >
                                    <AccordionSummaryContent>
                                        <AccordionTitle variant="subtitle2">
                                            {categoryName}
                                        </AccordionTitle>
                                        <AccordionDescription>
                                            {category.description}
                                        </AccordionDescription>
                                    </AccordionSummaryContent>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {Object.keys(category.events).flatMap(objectType =>
                                        category.events[objectType].map(eventType => {
                                            const eventMeta = notificationMeta[objectType]?.[eventType];
                                            const eventLabel = eventMeta?.label || eventType;
                                            const channels = eventMeta?.channels || [];
                                            const supportsEmail = channels.includes('email');
                                            const supportsApprise = channels.includes('apprise');
                                            const hasChannels = notificationChannels.length > 0 && notificationChannels.some(ch => ch.enabled);

                                            return (
                                                <NotificationEventBlock key={`${categoryName}-${objectType}-${eventType}`}>
                                                    <NotificationEventTitle variant="body2">
                                                        {eventLabel}
                                                    </NotificationEventTitle>
                                                    {supportsEmail && (
                                                        <PreferenceOption>
                                                            <PreferenceOptionLabel
                                                                onClick={() => {
                                                                    const currentValue = preferences.notifications.email[objectType]?.[eventType] ?? true;
                                                                    handleNotificationChange('email', objectType, eventType, !currentValue);
                                                                }}
                                                            >
                                                                <PreferenceOptionTitle>Email</PreferenceOptionTitle>
                                                            </PreferenceOptionLabel>
                                                            <Switch
                                                                checked={preferences.notifications.email[objectType]?.[eventType] ?? true}
                                                                onChange={(e) => handleNotificationChange('email', objectType, eventType, e.target.checked)}
                                                                size="small"
                                                            />
                                                        </PreferenceOption>
                                                    )}

                                                    {supportsApprise && (
                                                        <PreferenceOption>
                                                            <PreferenceOptionLabelDisabled
                                                                onClick={() => {
                                                                    if (!hasChannels) return;
                                                                    const currentValue = preferences.notifications.apprise[objectType]?.[eventType] ?? true;
                                                                    handleNotificationChange('apprise', objectType, eventType, !currentValue);
                                                                }}
                                                                disabled={!hasChannels}
                                                            >
                                                                <PreferenceOptionTitle>Apprise</PreferenceOptionTitle>
                                                            </PreferenceOptionLabelDisabled>
                                                            <Switch
                                                                checked={hasChannels && (preferences.notifications.apprise[objectType]?.[eventType] ?? true)}
                                                                onChange={(e) => handleNotificationChange('apprise', objectType, eventType, e.target.checked)}
                                                                disabled={!hasChannels}
                                                                size="small"
                                                            />
                                                        </PreferenceOption>
                                                    )}
                                                </NotificationEventBlock>
                                            );
                                        })
                                    )}
                                </AccordionDetails>
                            </NotificationAccordion>
                        );
                    })}
                </CardContent>
            </SettingsCard>

            {/* Preferences Section */}
            <SettingsCard>
                <CardContent>
                    <SectionTitle>
                        <SettingsIcon fontSize="medium" />
                        <SectionTitleText primary="Preferences" secondary="Customize how information is displayed and your regional settings." />
                    </SectionTitle>

                    <SecurityGridContainer container spacing={3}>
                        {/* Display Settings */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                Display Settings
                            </Typography>
                            <SettingsDescription variant="body2">
                                Customize how information is displayed.
                            </SettingsDescription>
                            <SettingsFormControl fullWidth>
                                <InputLabel>Language</InputLabel>
                                <Select value="en" label="Language">
                                    <MenuItem value="en">English</MenuItem>
                                </Select>
                            </SettingsFormControl>
                            <SettingsFormControl fullWidth>
                                <Autocomplete
                                    value={preferences.timezone || "UTC"}
                                    onChange={(_, value) => handlePreferenceChange('timezone', value || "UTC")}
                                    options={TIMEZONES}
                                    disableClearable
                                    renderInput={(params) => <TextField {...params} label="Time Zone" />}
                                />
                            </SettingsFormControl>
                            <FormControl fullWidth>
                                <InputLabel>Date Format</InputLabel>
                                <Select value="YYYY-MM-DD" label="Date Format">
                                    <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                                    <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                                    <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Regional Settings */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                Regional Settings
                            </Typography>
                            <SettingsDescription variant="body2">
                                Regional formatting preferences.
                            </SettingsDescription>
                            <SettingsFormControl fullWidth>
                                <InputLabel>Currency</InputLabel>
                                <Select value="USD" label="Currency">
                                    <MenuItem value="USD">USD ($)</MenuItem>
                                    <MenuItem value="EUR">EUR (€)</MenuItem>
                                    <MenuItem value="GBP">GBP (£)</MenuItem>
                                </Select>
                            </SettingsFormControl>
                            <NumberFormatField
                                fullWidth
                                label="Number Format"
                                value="1,234.56"
                                disabled
                            />
                            <FormControl fullWidth>
                                <InputLabel>Week Starts On</InputLabel>
                                <Select value="monday" label="Week Starts On">
                                    <MenuItem value="sunday">Sunday</MenuItem>
                                    <MenuItem value="monday">Monday</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </SecurityGridContainer>
                </CardContent>
            </SettingsCard>

            {/* Page Footer */}
            <PageFooter>
                <Button variant="outlined" onClick={() => window.history.back()}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSavePreferences}>
                    Save All Changes
                </Button>
            </PageFooter>

            {/* Two-Factor Authentication Dialog */}
            <Dialog open={twoFactorDialog.open} onClose={twoFactorBusy ? undefined : closeTwoFactorDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {twoFactorDialog.mode === 'enable' && twoFactorDialog.step === 1 && 'Set up Two-Factor Authentication'}
                    {twoFactorDialog.mode === 'enable' && twoFactorDialog.step === 3 && 'Save Your Backup Codes'}
                    {twoFactorDialog.mode === 'disable' && 'Disable Two-Factor Authentication'}
                    {twoFactorDialog.mode === 'regenerate' && twoFactorDialog.step === 1 && 'Regenerate Backup Codes'}
                    {twoFactorDialog.mode === 'regenerate' && twoFactorDialog.step === 3 && 'New Backup Codes'}
                </DialogTitle>
                <DialogContent>
                    {twoFactorDialog.mode === 'enable' && twoFactorDialog.step === 1 && (
                        <Box>
                            <Typography variant="body2" gutterBottom>
                                Scan this QR code with your authenticator app, or enter the secret manually.
                            </Typography>
                            {twoFactorForm.otpauthUrl && (
                                <QRCodeBox>
                                    <QRCodeSVG value={twoFactorForm.otpauthUrl} size={192} level="M" includeMargin={false} />
                                </QRCodeBox>
                            )}
                            <Typography variant="caption" color="textSecondary">Secret (groups of 4):</Typography>
                            <SecretBox>{formatSecretGroups(twoFactorForm.secret)}</SecretBox>
                            <CopySecretRow>
                                <Button size="small" onClick={copySecret}>Copy secret</Button>
                            </CopySecretRow>
                            <PasswordField
                                fullWidth
                                type="password"
                                label="Current Password"
                                autoComplete="current-password"
                                value={twoFactorForm.password}
                                onChange={(e) => setTwoFactorForm((f) => ({ ...f, password: e.target.value }))}
                            />
                            <PasswordField
                                fullWidth
                                type="text"
                                label="Authenticator Code"
                                value={twoFactorForm.code}
                                onChange={(e) => setTwoFactorForm((f) => ({ ...f, code: e.target.value }))}
                                helperText="Enter the 6-digit code from your authenticator app."
                            />
                            {twoFactorError && <Alert severity="error">{twoFactorError}</Alert>}
                        </Box>
                    )}

                    {twoFactorDialog.mode === 'enable' && twoFactorDialog.step === 3 && (
                        <Box>
                            <TwoFactorWarningAlert severity="warning">
                                These backup codes are shown only once. Store them in a safe place — each works only once if you lose access to your authenticator.
                            </TwoFactorWarningAlert>
                            <BackupCodeGrid>
                                {twoFactorBackupCodes.map((c) => (
                                    <BackupCodeText key={c}>{c}</BackupCodeText>
                                ))}
                            </BackupCodeGrid>
                            <Button onClick={copyBackupCodes} variant="outlined" fullWidth>Copy All Codes</Button>
                        </Box>
                    )}

                    {twoFactorDialog.mode === 'disable' && (
                        <Box>
                            <Typography variant="body2" gutterBottom>
                                Confirm with your password and a current authenticator code or backup code.
                            </Typography>
                            <PasswordField
                                fullWidth
                                type="password"
                                label="Current Password"
                                autoComplete="current-password"
                                value={twoFactorForm.password}
                                onChange={(e) => setTwoFactorForm((f) => ({ ...f, password: e.target.value }))}
                            />
                            <PasswordField
                                fullWidth
                                type="text"
                                label="Authenticator or Backup Code"
                                value={twoFactorForm.code}
                                onChange={(e) => setTwoFactorForm((f) => ({ ...f, code: e.target.value }))}
                            />
                            {twoFactorError && <Alert severity="error">{twoFactorError}</Alert>}
                        </Box>
                    )}

                    {twoFactorDialog.mode === 'regenerate' && twoFactorDialog.step === 1 && (
                        <Box>
                            <TwoFactorWarningAlert severity="warning">
                                Regenerating will invalidate your existing backup codes.
                            </TwoFactorWarningAlert>
                            <PasswordField
                                fullWidth
                                type="password"
                                label="Current Password"
                                autoComplete="current-password"
                                value={twoFactorForm.password}
                                onChange={(e) => setTwoFactorForm((f) => ({ ...f, password: e.target.value }))}
                            />
                            <PasswordField
                                fullWidth
                                type="text"
                                label="Authenticator or Backup Code"
                                value={twoFactorForm.code}
                                onChange={(e) => setTwoFactorForm((f) => ({ ...f, code: e.target.value }))}
                            />
                            {twoFactorError && <Alert severity="error">{twoFactorError}</Alert>}
                        </Box>
                    )}

                    {twoFactorDialog.mode === 'regenerate' && twoFactorDialog.step === 3 && (
                        <Box>
                            <TwoFactorWarningAlert severity="warning">
                                Store these backup codes safely — each works only once and they're shown only this once.
                            </TwoFactorWarningAlert>
                            <BackupCodeGrid>
                                {twoFactorBackupCodes.map((c) => (
                                    <BackupCodeText key={c}>{c}</BackupCodeText>
                                ))}
                            </BackupCodeGrid>
                            <Button onClick={copyBackupCodes} variant="outlined" fullWidth>Copy All Codes</Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {twoFactorDialog.mode === 'enable' && twoFactorDialog.step === 1 && (
                        <>
                            <Button onClick={closeTwoFactorDialog} disabled={twoFactorBusy}>Cancel</Button>
                            <Button onClick={submitEnableTwoFactor} variant="contained" disabled={twoFactorBusy}>Enable</Button>
                        </>
                    )}
                    {twoFactorDialog.mode === 'enable' && twoFactorDialog.step === 3 && (
                        <Button onClick={closeTwoFactorDialog} variant="contained">I've saved my codes</Button>
                    )}
                    {twoFactorDialog.mode === 'disable' && (
                        <>
                            <Button onClick={closeTwoFactorDialog} disabled={twoFactorBusy}>Cancel</Button>
                            <Button onClick={submitDisableTwoFactor} variant="contained" color="error" disabled={twoFactorBusy}>Disable</Button>
                        </>
                    )}
                    {twoFactorDialog.mode === 'regenerate' && twoFactorDialog.step === 1 && (
                        <>
                            <Button onClick={closeTwoFactorDialog} disabled={twoFactorBusy}>Cancel</Button>
                            <Button onClick={submitRegenerateBackupCodes} variant="contained" disabled={twoFactorBusy}>Regenerate</Button>
                        </>
                    )}
                    {twoFactorDialog.mode === 'regenerate' && twoFactorDialog.step === 3 && (
                        <Button onClick={closeTwoFactorDialog} variant="contained">I've saved my codes</Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Add Channel Dialog */}
            <Dialog open={addChannelDialogOpen} onClose={handleCloseAddChannelDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add Notification Channel</DialogTitle>
                <DialogContent>
                    <FirstFormField>
                        <TextField
                            fullWidth
                            label="Name"
                            value={newChannel.name}
                            onChange={(e) => {
                                setNewChannel({ ...newChannel, name: e.target.value });
                                if (channelErrors.name) {
                                    setChannelErrors({ ...channelErrors, name: '' });
                                }
                            }}
                            error={!!channelErrors.name}
                            helperText={channelErrors.name || 'Enter a descriptive name for this channel'}
                            required
                        />
                    </FirstFormField>
                    <FormField>
                        <TextField
                            fullWidth
                            label="Apprise URL"
                            value={newChannel.url}
                            onChange={(e) => {
                                setNewChannel({ ...newChannel, url: e.target.value });
                                if (channelErrors.url) {
                                    setChannelErrors({ ...channelErrors, url: '' });
                                }
                            }}
                            error={!!channelErrors.url}
                            helperText={channelErrors.url || 'Enter the Apprise notification URL (e.g., discord://webhook_id/webhook_token)'}
                            placeholder="discord://webhook_id/webhook_token"
                            required
                        />
                    </FormField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddChannelDialog}>Cancel</Button>
                    <Button onClick={handleSaveChannel} variant="contained">
                        Add Channel
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!revokeDeviceTarget}
                onClose={() => setRevokeDeviceTarget(null)}
                onConfirm={handleRevokeTrustedDevice}
                title="Revoke Trusted Device"
                message={`Revoke trust for ${revokeDeviceTarget?.name || 'this device'}? It will require 2FA on next sign-in.`}
                confirmLabel="Revoke"
            />

            <ConfirmDialog
                open={revokeAllOpen}
                onClose={() => setRevokeAllOpen(false)}
                onConfirm={handleRevokeAllTrustedDevices}
                title="Revoke All Trusted Devices"
                message="Revoke trust for every device? Every device will require 2FA on next sign-in."
                confirmLabel="Revoke All"
            />
        </SettingsContainer>
    );
};

export default Settings;
